<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Schedule page for external users -->
<template>
  <div class="pi-scheduler page">
    <header>
      <div class="logo-wrap">
        <div class="logo">
          <img src="../assets/osp.png">
        </div>
        <h3>OSP Proposal Review Scheduling System</h3>
      </div>
    </header>

    <div class="scheduler-wrapper">
      <div class="schedule-message" v-if="!shouldShowScheduler">
        <!-- <p><b>The Proposal Scheduling System should be used for the following types of proposal reviews:</b></p>
        <ul>
          <li>Federal and Non-federal Comprehensive Review (Proposal developed in response to sponsor’s solicitation)</li>
          <li>Letter of intent, Pre-proposal, or Whitepapers (Preliminary communication submitted to sponsor in response to sponsor’s solicitation)</li>
        </ul>
        <p>To schedule a proposal review appointment for the same day as the sponsor-imposed deadline, you will need to request an <b>Essential Component Review</b> by contacting <a href="mailto:osp@unm.edu">osp@unm.edu</a></p>
        <p><b>Please note:</b> For <b>Essential Component Reviews</b>, the PI is solely responsible for proposal materials outside of the OSP reviewed items. For more information on the difference between Comprehensive and Essential Component Reviews, please visit the <a href="https://osp.unm.edu/proposals/index.html">Proposal Review Matrix</a>.</p>
        <p><b>Non-Competitive Opportunities</b></p>
        <p>In some cases, sponsors make opportunities available directly to PIs without a formal solicitation from the sponsor, typically in the form of contracts. If you have a non-competitive opportunity, create a Cayuse record. Once you are routing the record, contact OSP Contracts Team at <a href="mailto:ospcontracts@unm.edu">ospcontracts@unm.edu</a> to start the Contract Review and Negotiation Process. <i>No proposal appointment is necessary for non-competitive opportunities.</i></p>
        <p>While scheduling your appointment, please be prepared to identify specific solicitation requirements on the appointment request form.</p>
        <p>*For general scheduling questions please contact <a href="mailto:osp@unm.edu">osp@unm.edu</a></p> -->

        <!-- <av-button @click="shouldShowScheduler = true">Schedule Review</av-button> -->
      </div>

      <appointment-scheduler v-if="shouldShowScheduler" @scheduled="handleScheduled" @cancel="shouldShowScheduler = false"></appointment-scheduler>

      <div class="upcoming-appointments" >
        <div v-if="!shouldShowScheduler">
            <h3 class="header">Upcoming Appointment{{ appointments.length > 1 ? 's' : ''}}
            <av-button class="schedule-button" color="success" @click="shouldShowScheduler = true"><i class="fal fa-calendar-plus"></i> Schedule Review</av-button>
            </h3>
      </div>
      <div class="upcoming-appointments" v-if="!shouldShowScheduler && appointments.length > 0">
        <div class="upcoming-appointment" v-for="appointment of appointments" :key="appointment.id">
        <div class="header">
            <h5>{{ appointment.appointmentType.name  }}</h5>
            <span><i class="fal fa-calendar"></i> {{ formatTime(appointment.start, 'MMMM do yyyy \'at\' h:mma') }}</span>
        </div>
        <p>
            {{ appointment.appointmentType.description }}
            <br><br>
            <label class="av-label">PI</label>
            {{ appointment.piFirstName }} {{ appointment.piLastName }}
            <br><br>
            <label class="av-label">Proposal Record Number</label>
            {{ formatProposalType(appointment.proposalNumberType) }}{{ appointment.proposalNumber }}
        </p>
        <div class="actions">
            <a class="shaded black" @click="rescheduleAppointment(appointment.id)"><i class="fal fa-calendar"></i> Reschedule</a>
            <a class="shaded red" @click="cancelAppointment(appointment.id)">Cancel</a>
            <a class="shaded black" target="_blank" :href="'/api/appointment/' + appointment.id + '/ics'" style="text-decoration: none;"><i class="fal fa-calendar-plus"></i> Download ics</a>
        </div>
        </div>
      </div>
      <div class="no-appointments" v-if="!shouldShowScheduler && appointments.length == 0">
          <h4>No scheduled appointments at this time</h4>
      </div>
      </div>
    </div>

    <!-- ReSchedule Modal -->
    <av-modal class="reschedule-modal" :visible="shouldShowRescheduler" @close="shouldShowRescheduler = false">
      <div class="modal-header">
        {{ 'Reschedule' }}
        {{ selectedAppointment.appointmentType.name }}
        <div class="time-info">
          <i class="fal fa-calendar"></i> {{ formatTime(selectedAppointment.start, 'MMM do yyyy') }}{{ formatTime(selectedAppointment.start,  ' @ hh:mma') }}
        </div>
      </div>
      <div class="re-assign-insctruction">
        <h5>Select New Date:</h5>
      </div>
      <div class="modal-content">
        <appointment-slot-selection
          :appointmentTypeId="selectedAppointment.appointmentType.id"
          :currentReviewerId="0"
          :forReschedule="true"
          :internalScheduling="false"
          :maxDate="endOfDayDeadline()"
          :startTime="selectedAppointment.start"
          @slot-selected="handleSlotSelection"
        ></appointment-slot-selection>
      </div>
      <div class="modal-actions">
        <a class="shaded red" @click="resetReschedule">Cancel</a>
      </div>
    </av-modal>
  </div>
</template>

<script lang="ts">
import { format } from 'date-fns'
import { defineComponent, PropType } from 'vue'
import { mapState } from 'vuex'
import AppointmentService from '../common/appointmentService'
import AppointmentScheduler from '../components/AppointmentScheduler.vue'
import Appointment from '../models/appointment'

import addDays from 'date-fns/addDays'
import AvailabilitySlot from '../models/availabilitySlot'
import AppointmentSlotSelection from '../components/AppointmentSlotSelection.vue'
import { cloneDeep } from 'lodash'
import UserReassign from '../models/userReassign'
import { AxiosError } from 'axios'
import { ProposalNumberType } from '../models/proposalType'

export default defineComponent({
  name: 'Schedule',

  props: {
    appointment: {
      type: Object as PropType<Appointment>,
      required: true
    },
    visible: {
      type: Boolean,
      required: true,
      default: false
    }
  },

  data () {
    return {
      isActive: true,

      /** List of scheduled appointments for the current user */
      appointments: [] as Array<Appointment>,
      selectedAppointment: null as unknown as Appointment,
      reviewDeadline: null as unknown as Date,
      shouldShowRescheduler: false,

      availableReviewers: [] as Array<UserReassign>,
      /** Indicates if the scheduler interface should be shown */
      shouldShowScheduler: false
    }
  },

  computed: {
    ...mapState(['userId']),

    /**
     * Sorts available reviewers by workload
     */
    sortedAvailableReviewers (): Array<UserReassign> {
      const reviewers = cloneDeep(this.availableReviewers)
        .sort((a, b) => {
          if (a.workload > b.workload) {
            return 1
          } else if (a.workload < b.workload) {
            return -1
          } else {
            return 0
          }
        })

      return reviewers.filter(r => !r.hasExternalConflicts && r.availableForSlot)
    }
  },

  watch: {
    userId: {
      handler () {
        this.getAppointments()
      },
      immediate: true
    }
  },

  methods: {
    /**
     * The display value for the proposal number formatted to include the
     * proposal number type
     */
    proposalNumberDisplay (appointment: Appointment): string {
      return AppointmentService.formatProposalNumber(appointment)
    },

    /**
     * Sorts the current list of appointments
     */
    sortedAppointments (): Array<Appointment> {
      const appointments = cloneDeep(this.appointments)
        .sort((a, b) => {
          if (a.start > b.start) {
            return 1
          } else if (a.start < b.start) {
            return -1
          } else {
            return 0
          }
        })

      return appointments
    },

    /**
     * Add one to the date, without time it will change it to midnight of the review deadline
     */
    endOfDayDeadline (): Date {
      this.reviewDeadline = addDays(new Date(this.selectedAppointment.reviewDeadline), 1)
      return this.reviewDeadline
    },

    /**
     * Handles an appointment being scheduled
     */
    handleScheduled () {
      setTimeout(() => {
        this.shouldShowScheduler = false
        this.getAppointments()
      }, 3000)
    },

    /**
     * get appointments for the date range
     */
    getAppointments () {
      AppointmentService.getForProjectUser(this.userId, true).then(res => {
        res.data.forEach(app => {
          app.start = new Date(app.start)
          app.end = new Date(app.end)
        })
        this.appointments = res.data
      })
    },

    /**
     * Formats the given date in the specified format
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    formatProposalType (proposalType: ProposalNumberType): string {
      return proposalType === 'QuickProposal' ? 'QP-' : 'PD-'
    },

    /**
     * Cancel selected appointment
     * @param appointemntId
     */
    cancelAppointment (appointemntId: number) {
      if (confirm('Are you sure you want to cancel this appointment?')) {
        AppointmentService.cancelAppointment(appointemntId).then(() => {
          const ind = this.appointments.findIndex(app => app.id === appointemntId)
          this.appointments.splice(ind, 1)
        })
      }
    },

    /**
     * Reschedule selected appointment to another slot
     * @param appointmentId
     */
    rescheduleAppointment (appointmentId: number) {
      const appt = this.appointments.find(app => app.id === appointmentId)
      this.selectedAppointment = appt as Appointment
      this.shouldShowRescheduler = true
    },

    /**
     * reset reschedule form
     */
    resetReschedule () {
      this.shouldShowRescheduler = false
    },

    /**
     * Once a new slot is selected for rescheduling, this will be executed
     * @param slot
     */
    handleSlotSelection (slot: AvailabilitySlot) {
      if (confirm('Are you sure you want to reschedule this appointment until ' + slot.start.toLocaleString() + '?')) {
        const params = {} as any
        // const reviewers = this.getReviewerForReschedule(slot)
        // if (reviewers.length > 0) {
        //   params.reviewerId = reviewers[0].id
        // } else {
        //   params.reviewerId = 0
        // }

        params.startTime = slot.start
        params.appointmentTypeId = slot.appointmentType.id

        if (this.selectedAppointment.appointmentTypeOffering.id !== 0) {
          params.appointmentTypeOfferingId = this.selectedAppointment.appointmentTypeOffering.id
        }
        const allFields = cloneDeep(this.selectedAppointment.intakeFormResponse)
        params.intakeFormResponse = allFields

        AppointmentService.rescheduleAppointment(this.selectedAppointment.id, params, false).then(res => {
          this.resetReschedule()
          this.shouldShowRescheduler = false
          res.data.start = new Date(res.data.start)
          res.data.end = new Date(res.data.end)
          res.data.scheduledOn = new Date(res.data.scheduledOn)
          res.data.reviewDeadline = new Date(res.data.reviewDeadline)
          const ind = this.appointments.findIndex(app => app.id === this.selectedAppointment.id)
          this.appointments.splice(ind, 1, res.data)
          this.isActive = true
        }).catch((err: AxiosError) => {
          if (err.response?.status === 409) {
            alert('No longer available.')
          }
        })
      }
      this.appointments = this.sortedAppointments()
    }

    // getReviewerForReschedule (slot: AvailabilitySlot): Array<UserReassign> {
    //   AppointmentService.reviewerAvailability(slot.appointmentTypeOffering.id, slot.start).then(rev => {
    //     this.availableReviewers = rev.data
    //   })

    //   this.availableReviewers.forEach(rev => {
    //     if (rev.hasExternalConflicts || !rev.availableForSlot) {
    //       const ind = this.availableReviewers.findIndex(ar => ar.id === rev.id)
    //       this.availableReviewers.splice(ind, 1)
    //     }
    //   })

    //   return this.sortedAvailableReviewers
    // }
  },

  components: {
    AppointmentScheduler,
    AppointmentSlotSelection
  }
})
</script>
